import React, { useState, useEffect } from 'react';
import { useFileContext } from '../../ContextAPI/FileContext';
import './ToggleSwitch.css';
import { ManagerlApi } from '../../API/api';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { FaTrash } from 'react-icons/fa';

const CSFTab = () => {
  const { setRunCSF } = useFileContext();
  const [folderPath, setFolderPath] = useState('');
  const [scenes, setScenes] = useState('SLOPE');
  const [slopeProcessing, setSlopeProcessing] = useState(true);
  const [clothResolution, setClothResolution] = useState(null);
  const [maxIterations, setMaxIterations] = useState(null);
  const [classThreshold, setClassThreshold] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState('custom');
  const [showCheckMark, setShowCheckMark] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState('');
  const [requests, setRequests] = useState([]);


  const extractFolderId = (url) => {
    const parts = url.split('/');
    return parts[parts.length - 1];
  };

  const formatDateTime = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  const handlePresetChange = (e) => {
    const preset = e.target.value;
    setSelectedPreset(preset);
    if (preset === 'embankment') {
      setClothResolution(0.5);
      setMaxIterations(1000);
      setClassThreshold(0.6);
    } else if (preset === 'car_removal') {
      setClothResolution(1.0);
      setMaxIterations(1000);
      setClassThreshold(0.1);
    }
  };

  const handleRunCSF = async () => {
    const folderId = extractFolderId(folderPath);
    if (!folderId) {
      console.error('Invalid folder URL');
      return;
    }

    const apiUrl = `${ManagerlApi}/CSF/${folderId}`;

    setCurrentFolderId(folderId);
    setShowCheckMark(false);

    const requestId = uuidv4();
    const csf_params = {
      SCENES: scenes,
      CLOTH_RESOLUTION: parseFloat(clothResolution),
      MAX_ITERATION: parseInt(maxIterations),
      CLASS_THRESHOLD: parseFloat(classThreshold),
      PROC_SLOPE: Boolean(slopeProcessing),
    };

    const startTime = formatDateTime(new Date());
    setRequests((prevRequests) => [
      ...prevRequests,
      {
        id: requestId,
        folderId,
        status: 'In Progress',
        loading: true,
        startTime,
        csf_params,
        showParams: false,
      },
    ]);

    setFolderPath('');

    const requestBody = { csf_params };
    console.log(requestBody);

    try {
      const response = await axios.post(apiUrl, requestBody, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const statusMessage = response.data.message || 'Task Completed';
      setRequests((prevRequests) =>
        prevRequests.map((request) =>
          request.id === requestId
            ? { ...request, status: `${statusMessage} ✅`, loading: false }
            : request
        )
      );
    } catch (error) {
      console.error('Error running CSF!', error);

      const errorMessage = error.response?.data?.message || 'Task Failed';
      setRequests((prevRequests) =>
        prevRequests.map((request) =>
          request.id === requestId
            ? { ...request, status: `${errorMessage} ❌`, loading: false }
            : request
        )
      );
    }
  };

  const handleDeleteRequest = (id) => {
    setRequests((prevRequests) =>
      prevRequests.filter((request) => request.id !== id)
    );
  };

  const handleClearAll = () => {
    setRequests([]);
  };

  const toggleParamsVisibility = (id) => {
    setRequests((prevRequests) =>
      prevRequests.map((request) =>
        request.id === id ? { ...request, showParams: !request.showParams } : request
      )
    );
  };

  useEffect(() => {
    setRunCSF(() => handleRunCSF);
  }, [folderPath, setRunCSF]);

  return (
    <div>
      <div>
        <label>Folder Path (contains las files)</label>
        <input
          type='text'
          placeholder='Folder Name'
          value={folderPath}
          onChange={(e) => setFolderPath(e.target.value)}
        />
      </div>
      <div style={{ marginTop: '25px', display: 'flex', justifyContent: 'flex-start' }}>
        <div style={{ marginRight: '60px' }}>
          <h6>General Parameter Setting</h6>
          <div>
            <label>Scenes</label>
            <select
              value={scenes}
              onChange={(e) => setScenes(e.target.value)}
            >
              <option value="SLOPE">Steep slope</option>
              <option value="RELIEF">Relief</option>
              <option value="FLAT">Flat</option>
            </select>
          </div>
          <div>
            <label>
              <input
                type="checkbox"
                checked={slopeProcessing}
                onChange={(e) => setSlopeProcessing(e.target.checked)}
              />{" "}
              Slope processing
            </label>
          </div>
        </div>
      </div>
      <div>
        <label>Preset Parameters</label>
        <select
          value={selectedPreset}
          onChange={handlePresetChange}
          style={{ width: '300px', padding: '8px', marginTop: '10px' }}
        >
          <option value="custom">Custom</option>
          <option value="embankment">Embankment Analysis</option>
          <option value="car_removal">Car Removal</option>
        </select>
      </div>
      <div>
        <label>Cloth Resolution</label>
        <input
          type='number'
          placeholder='Cloth Resolution'
          value={clothResolution}
          step={0.1}
          onChange={(e) => setClothResolution(e.target.value)}
          disabled={selectedPreset !== 'custom'}
        />
      </div>
      <div>
        <label>Max Iterations</label>
        <input
          type='number'
          placeholder='Max Iterations'
          value={maxIterations}
          step={1}
          onChange={(e) => setMaxIterations(e.target.value)}
          disabled={selectedPreset !== 'custom'}
        />
      </div>
      <div>
        <label>Class Threshold</label>
        <input
          type='number'
          placeholder='Class Threshold'
          value={classThreshold}
          step={0.1}
          onChange={(e) => setClassThreshold(e.target.value)}
          disabled={selectedPreset !== 'custom'}
        />
      </div>
      <div>
        <button onClick={handleRunCSF}>
          Run CSF Process
        </button>
      </div>
      {showCheckMark && (
        <div className='check-mark'>Request {currentFolderId} Sent ✅</div>
      )}
      <div className='request-list'>
        <table>
          <thead>
            <tr>
              <th>Folder ID</th>
              <th>Start Time</th>
              <th>Status</th>
              <th>CSF Parameters</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((request) => (
              <React.Fragment key={request.id}>
                <tr>
                  <td>{request.folderId}</td>
                  <td>{request.startTime}</td>
                  <td>
                    {request.loading ? 'Running...' : request.status}
                  </td>
                  <td>
                    <button className='btn btn-secondary' onClick={() => toggleParamsVisibility(request.id)}>
                      {request.showParams ? 'Hide' : 'Show'}
                    </button>
                  </td>
                  <td>
                    <button onClick={() => handleDeleteRequest(request.id)}>
                      <FaTrash />
                    </button>
                  </td>
                </tr>
                {request.showParams && (
                  <tr>
                    <td colSpan={5}>
                      <pre>{JSON.stringify(request.csf_params, null, 2)}</pre>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        <button className='clear-all-button' onClick={handleClearAll}>
          Remove All
        </button>
      </div>
    </div>
  );
};

export default CSFTab;
